import { supabase } from "@/integrations/supabase/client";
import { getStudioClient } from "@/lib/studio";
import type { Segment } from "@/lib/studio/segments";
import galleryInterior from "@/assets/mp/gallery1.jpg.asset.json";

import galleryExterior from "@/assets/mp/hero2.jpg.asset.json";

export type PaintSampleCategory =
  | "living_room" | "bedroom" | "kitchen" | "bathroom"
  | "exterior" | "siding" | "brick" | "cabinets"
  | "front_door" | "deck";

export const SAMPLE_CATEGORY_LABELS: Record<PaintSampleCategory, string> = {
  living_room: "Living Room",
  bedroom: "Bedroom",
  kitchen: "Kitchen",
  bathroom: "Bathroom",
  exterior: "Home Exterior",
  siding: "Siding & Trim",
  brick: "Brick Exterior",
  cabinets: "Cabinets",
  front_door: "Front Door",
  deck: "Deck / Fence",
};

export type SampleColorChoice = {
  hex: string; name: string; number: string; brand: string; id: string;
};

export type PaintSample = {
  id: string;
  title: string;
  category: PaintSampleCategory;
  description: string | null;
  source_image_url: string;
  thumbnail_url: string | null;
  source_image_width: number | null;
  source_image_height: number | null;
  segments: Segment[];
  default_assignments: Record<string, SampleColorChoice>;
  is_featured: boolean;
  sort_order: number;
};

export async function listPublishedSamples(): Promise<PaintSample[]> {
  const { data, error } = await supabase
    .from("paint_samples")
    .select("id,title,category,description,source_image_url,thumbnail_url,source_image_width,source_image_height,segments,default_assignments,is_featured,sort_order")
    .eq("is_published", true)
    .order("is_featured", { ascending: false })
    .order("sort_order", { ascending: true })
    .order("created_at", { ascending: false });
  if (error) throw error;
  const rows = (data ?? []) as unknown as PaintSample[];
  // Always ensure the user has at least one interior and one exterior sample
  // to try, even before an admin has uploaded any curated samples.
  const merged = [...rows];
  for (const b of BUILTIN_SAMPLES) {
    if (!merged.some((s) => s.category === b.category)) merged.push(b);
  }
  return merged;
}

/**
 * Built-in fallback samples that let anyone try the Studio without
 * uploading a photo. These are handled specially in the studio route:
 * instead of duplicating them into a Supabase project, the studio runs
 * the normal AI detection pipeline directly against the image URL.
 */
export const BUILTIN_SAMPLES: PaintSample[] = [
  {
    id: "builtin:interior-living-room",
    title: "Bright Living Room",
    category: "living_room",
    description: "Try any wall color on a sun-filled living room. No upload needed.",
    source_image_url: galleryInterior.url,
    thumbnail_url: galleryInterior.url,
    source_image_width: null,
    source_image_height: null,
    segments: [],
    default_assignments: {},
    is_featured: true,
    sort_order: 9998,
  },
  {
    id: "builtin:exterior-home",
    title: "Classic Home Exterior",
    category: "exterior",
    description: "Recolor siding, trim, and the front door on a residential exterior.",
    source_image_url: galleryExterior.url,
    thumbnail_url: galleryExterior.url,
    source_image_width: null,
    source_image_height: null,
    segments: [],
    default_assignments: {},
    is_featured: true,
    sort_order: 9999,
  },
];

export function isBuiltinSample(sample: Pick<PaintSample, "id">): boolean {
  return sample.id.startsWith("builtin:");
}

export type SavedStudioProject = {
  id: string;
  name: string | null;
  source_image_url: string;
  source_image_width: number | null;
  source_image_height: number | null;
  updated_at: string;
  latest_render_url: string | null;
  autosave_state?: unknown | null;
};

export async function listMySavedProjects(includeArchived = false): Promise<SavedStudioProject[]> {
  const { data: user } = await supabase.auth.getUser();
  if (!user.user) return [];
  const client = await getStudioClient();
  let q = client
    .from("studio_projects")
    .select("id,name,source_image_url,source_image_width,source_image_height,updated_at,autosave_state,studio_versions(rendered_image_url,created_at)")
    .eq("user_id", user.user.id)
    .order("updated_at", { ascending: false })
    .limit(24);
  if (!includeArchived) q = q.is("archived_at", null);
  const { data, error } = await q;
  if (error) throw error;
  return (data ?? []).map((p: any) => {
    const versions = (p.studio_versions ?? []) as { rendered_image_url: string; created_at: string }[];
    const latest = versions.sort((a, b) => (a.created_at < b.created_at ? 1 : -1))[0];
    return {
      id: p.id,
      name: p.name,
      source_image_url: p.source_image_url,
      source_image_width: p.source_image_width,
      source_image_height: p.source_image_height,
      updated_at: p.updated_at,
      latest_render_url: latest?.rendered_image_url ?? null,
      autosave_state: p.autosave_state ?? null,
    };
  });
}


export async function archiveStudioProject(projectId: string): Promise<void> {
  const client = await getStudioClient();
  const { error } = await client
    .from("studio_projects")
    .update({ archived_at: new Date().toISOString() })
    .eq("id", projectId);
  if (error) throw error;
}

export async function unarchiveStudioProject(projectId: string): Promise<void> {
  const client = await getStudioClient();
  const { error } = await client
    .from("studio_projects")
    .update({ archived_at: null })
    .eq("id", projectId);
  if (error) throw error;
}

/** Persist current in-progress state (assignments, hidden segments, lighting). */
export async function saveAutosaveState(
  projectId: string,
  state: {
    assignments: Record<string, SampleColorChoice & { finish?: string }>;
    hidden: string[];
    lighting: string;
  },
): Promise<void> {
  const client = await getStudioClient();
  const { error } = await client
    .from("studio_projects")
    .update({ autosave_state: state as unknown as any })
    .eq("id", projectId);
  if (error) throw error;
}


/**
 * Duplicate a sample into a fresh studio_project for the current user (or anon).
 * Preserves the sample's pre-built segments and default color assignments
 * so the user can immediately edit without waiting for AI detection.
 */
export async function duplicateSampleToProject(
  sample: PaintSample,
  _anonToken: string,
): Promise<{ projectId: string }> {
  const { data: userRes } = await supabase.auth.getUser();
  const uid = userRes.user?.id;
  if (!uid) throw new Error("Please sign in to save this sample as a project.");
  const client = await getStudioClient();
  const { data: proj, error } = await client
    .from("studio_projects")
    .insert({
      name: sample.title,
      source_image_url: sample.source_image_url,
      source_image_width: sample.source_image_width,
      source_image_height: sample.source_image_height,
      user_id: uid,
      source_sample_id: sample.id,
    })
    .select("id")
    .single();
  if (error) throw error;
  return { projectId: proj.id };
}
